import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import axiosInstance from '../config/axios';
import { useCart } from '../contexts/CartContext';
import Loading from '../components/Loading';
import ErrorMessage from '../components/ErrorMessage';
import { formatPickupWindow, formatDateTime } from '../utils/dateTimeUtils';
import { useTranslation } from '../hooks/useTranslation';
import './ProductDetailPage.css';

interface Product {
  id: string;
  name_en: string;
  name_zh: string;
  description_en: string;
  description_zh: string;
  price_per_kg: number;
  minimum_order_quantity: number;
  order_increment: number | null;
  image_filename: string | null;
  is_active: boolean;
}

interface PickupDateWithAvailability {
  id: string;
  pickup_date: string;
  pickup_start_time: string;
  pickup_end_time: string;
  last_order_datetime: string | null;
  available_quantity_kg: number;
}

interface LocationWithDates {
  id: string;
  name: string;
  address: string;
  pickup_dates: PickupDateWithAvailability[];
}

const ProductDetailPage: React.FC = () => {
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { t, language } = useTranslation();
  
  const [expandedLocationId, setExpandedLocationId] = useState<string | null>(null);
  const [selectedPickupDateId, setSelectedPickupDateId] = useState<string | null>(null);
  const [quantity, setQuantity] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');

  // Fetch product details
  const { data: product, isLoading: loadingProduct, error: productError } = useQuery<Product>({
    queryKey: ['product', productId],
    queryFn: async () => {
      const response = await axiosInstance.get(`/api/products/${productId}`);
      return response.data;
    },
    enabled: !!productId,
  });

  // Fetch locations with available dates for this product
  const { data: locationsWithDates, isLoading: loadingLocations } = useQuery<LocationWithDates[]>({
    queryKey: ['productLocations', productId],
    queryFn: async () => {
      const response = await axiosInstance.get(`/api/pickup-dates/by-product/${productId}`);
      return response.data;
    },
    enabled: !!productId,
  });

  const handleQuantityChange = (value: string) => {
    setQuantity(value);
    setError('');
    setSuccessMessage('');

    if (!selectedPickupDateId || !product) return;

    const qty = parseFloat(value);
    const selectedDate = getSelectedPickupDate();

    if (!selectedDate) return;

    if (value !== '' && !isNaN(qty)) {
      if (qty <= 0) {
        setError(t('productDetail.quantityPositiveError'));
      } else if (qty < product.minimum_order_quantity) {
        setError(t('productDetail.quantityMinError').replace('{min}', product.minimum_order_quantity.toString()));
      } else if (product.order_increment) {
        // Check if quantity is a valid multiple of the increment starting from minimum
        const diff = qty - product.minimum_order_quantity;
        const remainder = diff % product.order_increment;
        if (Math.abs(remainder) > 0.001) { // Use small epsilon for floating point comparison
          const example1 = product.minimum_order_quantity + product.order_increment;
          const example2 = product.minimum_order_quantity + product.order_increment * 2;
          setError(
            t('productDetail.quantityIncrementError')
              .replace('{increment}', product.order_increment.toString())
              .replace('{min}', product.minimum_order_quantity.toString())
              .replace('{example1}', example1.toString())
              .replace('{example2}', example2.toString())
          );
        } else if (qty > selectedDate.available_quantity_kg) {
          setError(t('productDetail.quantityExceedsError').replace('{max}', selectedDate.available_quantity_kg.toString()));
        }
      } else if (qty > selectedDate.available_quantity_kg) {
        setError(t('productDetail.quantityExceedsError').replace('{max}', selectedDate.available_quantity_kg.toString()));
      }
    }
  };

  const getSelectedPickupDate = (): PickupDateWithAvailability | null => {
    if (!selectedPickupDateId || !locationsWithDates) return null;
    
    for (const location of locationsWithDates) {
      const date = location.pickup_dates.find(d => d.id === selectedPickupDateId);
      if (date) return date;
    }
    return null;
  };

  const getSelectedLocation = (): LocationWithDates | null => {
    if (!selectedPickupDateId || !locationsWithDates) return null;
    
    return locationsWithDates.find(location => 
      location.pickup_dates.some(d => d.id === selectedPickupDateId)
    ) || null;
  };

  const handleAddToCart = () => {
    if (!selectedPickupDateId) {
      setError(t('productDetail.selectDateError'));
      return;
    }

    if (!quantity || quantity === '') {
      setError(t('productDetail.quantityRequiredError'));
      return;
    }

    const qty = parseFloat(quantity);
    if (isNaN(qty) || qty <= 0) {
      setError(t('productDetail.quantityInvalidError'));
      return;
    }

    if (!product) return;

    // Validate minimum order quantity
    if (qty < product.minimum_order_quantity) {
      setError(t('productDetail.quantityMinError').replace('{min}', product.minimum_order_quantity.toString()));
      return;
    }

    // Validate order increment
    if (product.order_increment) {
      const diff = qty - product.minimum_order_quantity;
      const remainder = diff % product.order_increment;
      if (Math.abs(remainder) > 0.001) {
        setError(t('productDetail.quantityIncrementError').replace('{increment}', product.order_increment.toString()));
        return;
      }
    }

    const selectedDate = getSelectedPickupDate();
    const selectedLocation = getSelectedLocation();
    
    if (!selectedDate || !selectedLocation) {
      setError(t('productDetail.dateNoLongerAvailable'));
      return;
    }

    if (qty > selectedDate.available_quantity_kg) {
      setError(t('productDetail.quantityExceedsError').replace('{max}', selectedDate.available_quantity_kg.toString()));
      return;
    }

    // Add to cart
    addToCart({
      product_id: product.id,
      product_name: language === 'zh' ? product.name_zh : product.name_en,
      price_per_kg: product.price_per_kg,
      quantity_kg: qty,
      pickup_date_id: selectedPickupDateId,
      pickup_date: selectedDate.pickup_date,
      pickup_start_time: selectedDate.pickup_start_time,
      pickup_end_time: selectedDate.pickup_end_time,
      location_name: selectedLocation.name,
      location_address: selectedLocation.address,
    });

    setSuccessMessage(t('productDetail.addedToCart'));
    setQuantity('');
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const handleLocationClick = (locationId: string) => {
    if (expandedLocationId === locationId) {
      setExpandedLocationId(null);
      setSelectedPickupDateId(null);
      setQuantity('');
      setError('');
    } else {
      setExpandedLocationId(locationId);
      setSelectedPickupDateId(null);
      setQuantity('');
      setError('');
    }
  };

  const handlePickupDateSelect = (pickupDateId: string) => {
    setSelectedPickupDateId(pickupDateId);
    setQuantity('');
    setError('');
    setSuccessMessage('');
  };

  if (loadingProduct || loadingLocations) {
    return <Loading />;
  }

  if (productError || !product) {
    return <ErrorMessage message={t('productDetail.loadingError')} />;
  }

  const selectedDate = getSelectedPickupDate();

  return (
    <div className="product-detail-container">
      <button
        onClick={() => navigate('/products')}
        style={{
          marginBottom: '20px',
          padding: '8px 16px',
          backgroundColor: '#6c757d',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          cursor: 'pointer',
        }}
      >
        ← {t('productDetail.backToProducts')}
      </button>

      <div className="product-detail-grid">
        {/* Product Information */}
        <section>
          {product.image_filename && (
            <img
              src={`/images/${product.image_filename}`}
              alt={language === 'zh' ? product.name_zh : product.name_en}
              style={{
                width: '100%',
                maxHeight: '400px',
                objectFit: 'cover',
                borderRadius: '8px',
                marginBottom: '20px',
              }}
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          )}
          <h1 style={{ marginTop: 0 }}>
            {language === 'zh' ? product.name_zh : product.name_en}
          </h1>
          <p style={{ fontSize: '18px', color: '#666', lineHeight: '1.6' }}>
            {language === 'zh' ? product.description_zh : product.description_en}
          </p>
          <p style={{ fontSize: '28px', fontWeight: 'bold', color: '#007bff', marginTop: '20px' }}>
            €{product.price_per_kg.toFixed(2)} {t('products.perKg')} <span style={{ fontSize: '16px', fontWeight: 'normal', color: '#666' }}>({t('products.inclVat')})</span>
          </p>
          <div style={{ marginTop: '15px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
            <p style={{ margin: '5px 0', fontSize: '16px', color: '#333' }}>
              <strong>{t('productDetail.minimumOrder')}:</strong> {product.minimum_order_quantity} {t('common.kg')}
            </p>
            {product.order_increment && (
              <p style={{ margin: '5px 0', fontSize: '16px', color: '#333' }}>
                <strong>{t('productDetail.orderIncrement')}:</strong> {product.order_increment} {t('common.kg')}
              </p>
            )}
          </div>
        </section>

        {/* Pickup Location Selection */}
        <section>
          <h2>{t('productDetail.selectPickupLocationDate')}</h2>
          
          {locationsWithDates && locationsWithDates.length > 0 ? (
            <div style={{ display: 'grid', gap: '15px' }}>
              {locationsWithDates.map(location => (
                <div key={location.id} style={{ border: '1px solid #ddd', borderRadius: '8px', overflow: 'hidden' }}>
                  {/* Location Header - Clickable */}
                  <div
                    onClick={() => handleLocationClick(location.id)}
                    style={{
                      padding: '15px',
                      backgroundColor: expandedLocationId === location.id ? '#e7f3ff' : '#f8f9fa',
                      cursor: 'pointer',
                      borderBottom: expandedLocationId === location.id ? '2px solid #007bff' : 'none',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <h3 style={{ margin: '0 0 5px 0' }}>{location.name}</h3>
                        <p style={{ margin: 0, fontSize: '14px', color: '#666' }}>
                          {location.address}
                        </p>
                        <p style={{ margin: '5px 0 0 0', fontSize: '14px', color: '#28a745', fontWeight: 'bold' }}>
                          {location.pickup_dates.length} {location.pickup_dates.length !== 1 ? t('productDetail.datesAvailablePlural') : t('productDetail.datesAvailable')} {t('productDetail.available').toLowerCase()}
                        </p>
                      </div>
                      <span style={{ fontSize: '24px', color: '#007bff' }}>
                        {expandedLocationId === location.id ? '▼' : '▶'}
                      </span>
                    </div>
                  </div>

                  {/* Expanded Dates */}
                  {expandedLocationId === location.id && (
                    <div style={{ padding: '15px', backgroundColor: 'white' }}>
                      <h4 style={{ marginTop: 0, marginBottom: '15px' }}>{t('productDetail.availablePickupDates')}</h4>
                      <div style={{ display: 'grid', gap: '10px' }}>
                        {location.pickup_dates.map(date => (
                          <div key={date.id}>
                            <div
                              onClick={() => handlePickupDateSelect(date.id)}
                              style={{
                                padding: '12px',
                                border: selectedPickupDateId === date.id ? '2px solid #007bff' : '1px solid #ddd',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                backgroundColor: selectedPickupDateId === date.id ? '#e7f3ff' : 'white',
                              }}
                            >
                              <p style={{ margin: '0 0 5px 0', fontWeight: 'bold' }}>
                                {formatPickupWindow(date.pickup_date, date.pickup_start_time, date.pickup_end_time, t('common.from'), t('common.to'))}
                              </p>
                              {date.last_order_datetime && (
                                <p style={{ margin: '5px 0', fontSize: '13px', color: '#dc3545' }}>
                                  {t('productDetail.orderBy')} {formatDateTime(date.last_order_datetime)}
                                </p>
                              )}
                              <p style={{ margin: '5px 0 0 0', fontSize: '14px', color: '#28a745' }}>
                                <strong>{t('productDetail.available')}:</strong> {date.available_quantity_kg} {t('common.kg')}
                              </p>
                            </div>

                            {/* Order Form - Shows when date is selected */}
                            {selectedPickupDateId === date.id && (
                              <div style={{ padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '8px', marginTop: '10px' }}>
                                <h4 style={{ marginTop: 0 }}>{t('productDetail.orderDetails')}</h4>
                                
                                <div style={{ marginBottom: '15px' }}>
                                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                                    {t('productDetail.quantityKg')}:
                                  </label>
                                  <input
                                    type="number"
                                    min={product.minimum_order_quantity}
                                    step={product.order_increment || 0.1}
                                    value={quantity}
                                    onChange={(e) => handleQuantityChange(e.target.value)}
                                    style={{
                                      width: '100%',
                                      padding: '10px',
                                      border: error ? '1px solid #dc3545' : '1px solid #ddd',
                                      borderRadius: '4px',
                                      fontSize: '16px',
                                    }}
                                    placeholder={
                                      product.order_increment 
                                        ? t('productDetail.quantityPlaceholderWithIncrement')
                                            .replace('{min}', product.minimum_order_quantity.toString())
                                            .replace('{increment}', product.order_increment.toString())
                                        : t('productDetail.quantityPlaceholder')
                                            .replace('{min}', product.minimum_order_quantity.toString())
                                    }
                                  />
                                  {error && (
                                    <p style={{ color: '#dc3545', margin: '5px 0 0 0', fontSize: '14px' }}>
                                      {error}
                                    </p>
                                  )}
                                </div>

                                {quantity && !error && parseFloat(quantity) > 0 && (
                                  <div style={{ marginBottom: '15px', padding: '10px', backgroundColor: 'white', borderRadius: '4px' }}>
                                    <p style={{ margin: '5px 0', fontSize: '16px' }}>
                                      <strong>{t('productDetail.subtotal')}:</strong> €{(parseFloat(quantity) * product.price_per_kg).toFixed(2)}
                                    </p>
                                  </div>
                                )}

                                <button
                                  onClick={handleAddToCart}
                                  disabled={!quantity || !!error}
                                  style={{
                                    width: '100%',
                                    padding: '12px',
                                    backgroundColor: !quantity || error ? '#ccc' : '#28a745',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    fontSize: '16px',
                                    fontWeight: 'bold',
                                    cursor: !quantity || error ? 'not-allowed' : 'pointer',
                                  }}
                                >
                                  {t('productDetail.addToCart')}
                                </button>

                                {successMessage && (
                                  <p style={{ color: '#28a745', margin: '10px 0 0 0', textAlign: 'center', fontWeight: 'bold' }}>
                                    {successMessage}
                                  </p>
                                )}

                                <button
                                  onClick={() => navigate('/cart')}
                                  style={{
                                    width: '100%',
                                    marginTop: '10px',
                                    padding: '12px',
                                    backgroundColor: '#007bff',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    fontSize: '16px',
                                    cursor: 'pointer',
                                  }}
                                >
                                  {t('productDetail.viewCart')}
                                </button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p style={{ color: '#666' }}>
              {t('productDetail.noPickupDates')}
            </p>
          )}
        </section>
      </div>
    </div>
  );
};

export default ProductDetailPage;
